/**
 * Recovery for a tab running a bundle the workers no longer accept.
 *
 * The sibling case is stale-chunk-reload.ts, which rescues a tab whose hashed
 * chunks have been deleted. That only fires when the tab *navigates* to a split
 * route. Someone who stays on one route and keeps searching never imports a new
 * chunk, so it never fires for them — and on 2026-07-30, with enforcement newly
 * 'on', that was exactly the shape of the users being refused: a Thai client on
 * our own dev-signed bundle from 2026-07-29, searching over and over, 403 every
 * time, for at least the 19 minutes we watched. Nothing prompted a reload, so
 * nothing fixed it.
 *
 * The 403 itself is the signal this uses instead.
 *
 * Only some refusals are worth reloading for, which is why the worker sends a
 * `reason` (see enforceDeployToken in both workers):
 *
 *   bad_signature / expired / future_build_time / malformed
 *      Our token is real but wrong — a stale bundle, or a rotated signing key.
 *      Reloading fetches the current bundle and its current token, which fixes
 *      it. Worth one reload.
 *
 *   missing_header
 *      We sent X-T2E-Client and the worker saw nothing, so something between us
 *      stripped it — a proxy, an extension. Any bundle we reload onto sends the
 *      same header and loses it the same way. Reloading here would spend a page
 *      load to arrive back at the identical 403, so this goes straight to the
 *      sign-in path, which does work (see auth-gate.ts).
 */
import { deployTokenId } from 'api/deploy-token'

const RELOAD_MARKER = 't2e:staleBundleReload'

// Hard ceiling on reloads per tab, independent of the identity check below.
// Two is enough for the only sequence that legitimately needs more than one:
// stale bundle -> reload -> land on a bundle that is *also* refused, which
// means the deployed bundle is the broken one and no further reload can help.
const MAX_ATTEMPTS = 2

// Deliberately not every rejection: see the doc comment. 'missing_header' is
// the one a reload provably cannot fix.
const RELOADABLE = new Set(['bad_signature', 'expired', 'future_build_time', 'malformed'])

/**
 * What we reloaded away from, and how many times this tab has done it.
 *
 * Keyed on the *bundle*, not on a timestamp. An earlier draft used a 60-second
 * window, which quietly re-armed: with a broken bundle deployed, every refusal
 * more than a minute after the last reload started another one, so the tab
 * reloaded once a minute for as long as the deploy stayed broken — wiping the
 * user's input each time. Time was never the right question. Reloading can only
 * help if it lands the tab on a different build, so the question is "have I
 * already tried reloading away from *this* build", and that answer never
 * expires.
 */
type Attempt = { token: string; attempts: number }

const readAttempt = (): Attempt | null => {
   const raw = window.sessionStorage.getItem(RELOAD_MARKER)
   if (raw === null) return null
   try {
      const parsed: unknown = JSON.parse(raw)
      const { token, attempts } = (parsed ?? {}) as Partial<Attempt>
      if (typeof token !== 'string' || typeof attempts !== 'number') return null
      return { token, attempts }
   } catch {
      // Corrupt or from an older format: treat as no attempt yet. The identity
      // check below still bounds this to one reload per bundle.
      return null
   }
}

/**
 * Reloads if a refusal looks like a stale bundle, and reports whether it did.
 *
 * `true` means a reload is under way and the caller should stop — the page is
 * being replaced, so putting a sign-in screen up now would flash and vanish.
 * `false` means the caller should carry on to its normal refusal handling.
 */
export const reloadIfStaleBundle = (body: unknown): boolean => {
   if (typeof window === 'undefined') return false

   const reason = (body as { error?: unknown; reason?: unknown } | null | undefined)?.reason
   if (typeof reason !== 'string' || !RELOADABLE.has(reason)) return false

   let previous: Attempt | null
   try {
      previous = readAttempt()
   } catch {
      // Safari in private mode throws on sessionStorage. With no way to record
      // an attempt, a reload could repeat without limit — so decline it. A
      // missed recovery costs the user a sign-in prompt; a reload loop costs
      // them the page.
      return false
   }

   // Already reloaded away from this exact build and still running it, so the
   // reload changed nothing and repeating it cannot start to work. This is what
   // makes a loop impossible: another reload needs a *different* build, which
   // needs a real deploy to have landed in between.
   if (previous && previous.token === deployTokenId) return false
   if (previous && previous.attempts >= MAX_ATTEMPTS) return false

   const next: Attempt = { token: deployTokenId, attempts: (previous?.attempts ?? 0) + 1 }
   try {
      window.sessionStorage.setItem(RELOAD_MARKER, JSON.stringify(next))
   } catch {
      return false
   }

   // src/server.ts sends the HTML `Cache-Control: no-cache`, which is what
   // makes this stick — without it the reload could be answered from cache with
   // the same stale document and the same dead token.
   window.location.reload()
   return true
}
